import { useCallback, useEffect, useRef, useState } from 'react';
import './Console.css';

/* ══════════════════════════════════════════════════════════════
   CONSOLE — a working shell. Type a command, get an answer.
   ══════════════════════════════════════════════════════════════ */

const PROMPT = 'visitor@rarespoanta:~$';

const LINKS = {
  rovaine: 'https://github.com/rarespoanta27/Rovaine',
  deadliq: 'https://github.com/rarespoanta27/DeadliQ',
  tuniq: 'https://github.com/rarespoanta27/TuniQ',
  github: 'https://github.com/rarespoanta27',
  linkedin: 'https://www.linkedin.com/in/rares-poanta-b3970b309',
};

const HELP = [
  ['whoami', 'who is behind this site'],
  ['projects', 'the products I have built'],
  ['stack', 'the tools I work with'],
  ['services', 'what you can hire me for'],
  ['open <name>', 'open rovaine · deadliq · tuniq · github · linkedin'],
  ['contact', 'how to reach me'],
  ['hire', 'start a project'],
  ['clear', 'wipe the screen'],
];

function run(raw, helpers) {
  const input = raw.trim();
  const [cmd, ...args] = input.toLowerCase().split(/\s+/);

  switch (cmd) {
    case '':
      return [];

    case 'help':
    case '?':
      return [
        { t: 'dim', v: 'Available commands' },
        ...HELP.map(([c, d]) => ({ t: 'kv', k: c, v: d })),
      ];

    case 'whoami':
      return [
        { t: 'ok', v: 'Poanta Rareș — full-stack software engineer' },
        { t: 'txt', v: 'Founder of AlgorX Software SRL, Timișoara, Romania.' },
        { t: 'txt', v: 'I design, build and operate web products end to end: interface,' },
        { t: 'txt', v: 'backend, database and the cloud infrastructure they run on.' },
        { t: 'txt', v: "Master's in Control Systems Engineering, UPT — in progress." },
      ];

    case 'projects':
    case 'ls':
      return [
        { t: 'dim', v: 'Selected builds' },
        { t: 'kv', k: 'rovaine', v: 'consumer platform · React · Node · Stripe   [OSS]' },
        { t: 'kv', k: 'deadliq', v: 'B2B SaaS · MERN · OCR · multi-tenant        [OSS]' },
        { t: 'kv', k: 'tuniq',   v: 'developer tooling · React · Python         [OSS]' },
        { t: 'txt', v: '' },
        { t: 'dim', v: 'Run "open rovaine" for the source, or scroll to section 01.' },
      ];

    case 'stack':
      return [
        { t: 'kv', k: 'frontend', v: 'React · Next.js · TypeScript · Tailwind' },
        { t: 'kv', k: 'backend',  v: 'Node · Express · Python · FastAPI · REST' },
        { t: 'kv', k: 'data',     v: 'PostgreSQL · MongoDB · Redis · MariaDB' },
        { t: 'kv', k: 'cloud',    v: 'AWS · GCP · Docker · Nginx · CI/CD · Linux' },
        { t: 'kv', k: 'payments', v: 'Stripe · JWT · OAuth' },
      ];

    case 'services':
      return [
        { t: 'ok', v: 'What you can hire me for' },
        { t: 'kv', k: '01', v: 'Web & product engineering — full build, front to back' },
        { t: 'kv', k: '02', v: 'SaaS platforms — auth, billing, dashboards, admin' },
        { t: 'kv', k: '03', v: 'Cloud & DevOps — deploys, CI/CD, hardening, cost review' },
        { t: 'kv', k: '04', v: 'Architecture review — audit an existing system' },
      ];

    case 'contact':
      return [
        { t: 'kv', k: 'email',    v: 'rarespoanta10@gmail.com' },
        { t: 'kv', k: 'linkedin', v: 'linkedin.com/in/rares-poanta-b3970b309' },
        { t: 'kv', k: 'github',   v: 'github.com/rarespoanta27' },
        { t: 'kv', k: 'based',    v: 'Timișoara, Romania — remote worldwide' },
        { t: 'kv', k: 'reply',    v: 'within 24 hours, on working days' },
      ];

    case 'hire':
    case 'start':
      helpers.mail();
      return [
        { t: 'ok', v: 'Opening your mail client…' },
        { t: 'txt', v: 'If nothing happened, write to rarespoanta10@gmail.com directly.' },
      ];

    case 'open': {
      const key = args[0];
      if (!key) return [{ t: 'err', v: 'usage: open <rovaine|deadliq|tuniq|github|linkedin>' }];
      if (!LINKS[key]) return [{ t: 'err', v: `unknown target: ${key}` }];
      helpers.open(LINKS[key]);
      return [{ t: 'ok', v: `Opening ${LINKS[key]} in a new tab…` }];
    }

    case 'uptime':
      return [
        { t: 'txt', v: '100+ projects shipped · 3 open-source products · 0 abandoned handovers' },
      ];

    case 'sudo':
      return [{ t: 'err', v: 'Nice try. This one is read-only.' }];

    case 'clear':
      helpers.clear();
      return [];

    default:
      return [{ t: 'err', v: `command not found: ${cmd} — type "help"` }];
  }
}

const BOOT = [
  { t: 'dim', v: 'AlgorX shell · type "help" for commands' },
  { t: 'txt', v: '' },
  { t: 'cmd', v: 'whoami' },
  ...run('whoami', {}),
  { t: 'txt', v: '' },
];

export default function Console() {
  const [history, setHistory] = useState(BOOT);
  const [value, setValue] = useState('');
  const [past, setPast] = useState([]);
  const [pastIdx, setPastIdx] = useState(-1);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const termRef = useRef(null);

  const helpers = {
    clear: () => setHistory([]),
    open: (u) => window.open(u, '_blank', 'noopener,noreferrer'),
    mail: () => { window.location.href = 'mailto:rarespoanta10@gmail.com'; },
  };

  const submit = useCallback((e) => {
    e.preventDefault();
    const raw = value;
    const out = run(raw, helpers);
    setHistory((h) => (raw.trim().toLowerCase() === 'clear' ? [] : [...h, { t: 'cmd', v: raw }, ...out]));
    if (raw.trim()) setPast((p) => [raw, ...p].slice(0, 30));
    setPastIdx(-1);
    setValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onKey = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const i = Math.min(past.length - 1, pastIdx + 1);
      if (i >= 0) { setPastIdx(i); setValue(past[i]); }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const i = pastIdx - 1;
      setPastIdx(i);
      setValue(i >= 0 ? past[i] : '');
    }
  };

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history]);

  return (
    <section id="console" className="band cs">
      <div className="shell">

        <div className="cs-grid">
          <div className="sec-head" data-rv="up">
            <span className="sec-tag"><b>03</b> — Console</span>
            <h2 className="display-lg">
              <span className="mask-line"><span>Ask the site</span></span>
              <span className="mask-line" style={{ '--ml-d': '110ms' }}><span>directly.</span></span>
            </h2>
            <p className="lede">
              A working shell, not a decoration. Type <code>help</code> to see what it knows,
              <code>projects</code> to list what is running, or <code>hire</code> to start
              a conversation without scrolling any further.
            </p>

            <div className="cs-suggest">
              {['help', 'whoami', 'projects', 'stack', 'contact'].map((c) => (
                <button
                  key={c}
                  className="cs-suggest-btn"
                  onClick={() => {
                    setHistory((h) => [...h, { t: 'cmd', v: c }, ...run(c, helpers)]);
                    inputRef.current?.focus();
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* ── Terminal ─────────────────────────────────── */}
          <div className="cs-term" ref={termRef} data-rv="zoom" onClick={() => inputRef.current?.focus()}>
            <div className="cs-term-bar">
              <span className="cs-term-dots"><i /><i /><i /></span>
              <span className="mono">bash — 80×24</span>
              <span className="cs-term-live"><i className="pulse" />session active</span>
            </div>

            <div className="cs-term-body" ref={bodyRef}>
              {history.map((l, i) => {
                if (l.t === 'cmd') {
                  return (
                    <p className="cs-line cs-line--cmd" key={i}>
                      <span className="cs-prompt">{PROMPT}</span> {l.v}
                    </p>
                  );
                }
                if (l.t === 'kv') {
                  return (
                    <p className="cs-line cs-line--kv" key={i}>
                      <span className="cs-key">{l.k}</span>
                      <span className="cs-val">{l.v}</span>
                    </p>
                  );
                }
                return <p className={`cs-line cs-line--${l.t}`} key={i}>{l.v || ' '}</p>;
              })}

              <form className="cs-input-row" onSubmit={submit}>
                <span className="cs-prompt">{PROMPT}</span>
                <input
                  ref={inputRef}
                  className="cs-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={onKey}
                  spellCheck="false"
                  autoComplete="off"
                  autoCapitalize="off"
                  aria-label="Terminal input"
                  placeholder="type a command…"
                />
              </form>
            </div>

            <div className="cs-term-foot">
              <span className="mono">↑ ↓ history</span>
              <span className="mono">enter to run</span>
              <span className="mono cs-term-hint">try: open rovaine</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
